import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Field, FormRenderProps, useField } from 'react-final-form';

import AdviceStatus from 'types/AdviceStatus';
import type PageBaseProps from 'types/PageBase';
import FormKeys from 'modules/form/types/FormKeys';
import type { Extra, ExtrasForm as ExtrasFormType } from 'types/ExtrasForm';

import { useSelector } from 'hooks';
import { getHyperlink } from 'utils/linkUtils';
import { formatUndefined } from 'utils/formUtils';
import usePreAuthorizedAmounts from 'hooks/usePreAuthorizedAmounts';

import {
  getGeneralTermsConditionsLink,
  getSelectedSelfService,
  isRequiredConsentOnWithdrawalRights as isRequiredConsentOnWithdrawalRightsSelector,
} from 'modules/selfServices/selectors';

// These imports needs to be direct imports from the components folder, *NOT* from ui/index to avoid circular dependency.
import Switch from 'components/ui/Switch';
import Button from 'components/ui/Button';
import PageFooter from 'components/ui/PageFooter';

import Content from './Content';

const Step: React.FC<FormRenderProps<ExtrasFormType> & Pick<PageBaseProps, 'onPrev' | 'shouldDisplayBackButton'>> = ({
  onPrev,
  handleSubmit,
  hasValidationErrors,
  shouldDisplayBackButton,
}) => {
  const selfService = useSelector(getSelectedSelfService);
  const generalTermsConditionsLink = useSelector(getGeneralTermsConditionsLink);
  const isRequiredConsentOnWithdrawalRights = useSelector(isRequiredConsentOnWithdrawalRightsSelector);

  const { value: extras = [] } = useField<Extra[]>(`${FormKeys.EXTRAS}.extras`, {
    subscription: { value: true }, format: formatUndefined,
  }).input;

  const { value: proactiveWorks = [] } = useField<Extra[]>(`${FormKeys.REPAIR_ORDER}.proactiveWorks`, {
    subscription: { value: true }, format: formatUndefined,
  }).input;

  const { hasCustomerPreAuthorizedAmount } = usePreAuthorizedAmounts();

  const isBasketEmpty = React.useMemo(
    () => {
      const hasAcceptedExtras = extras.some(({ status }) => status === AdviceStatus.ACCEPTED);
      const hasAcceptedProactiveWorks = proactiveWorks.some(({ status }) => status === AdviceStatus.ACCEPTED);
      const hasRepairOrder = selfService?.repairOrder && selfService?.repairOrder?.length > 0;
      return !hasAcceptedExtras && !hasAcceptedProactiveWorks && !hasRepairOrder && !hasCustomerPreAuthorizedAmount;
    },
    [extras, proactiveWorks, selfService?.repairOrder, hasCustomerPreAuthorizedAmount],
  );

  return (
    <div className="main-content">
      <main className="content">
        <Content />
        {!isBasketEmpty && (
          <div className="flex flex-col">
            {isRequiredConsentOnWithdrawalRights && (
              <Field name={`${FormKeys.POLICIES}.basketConsent`} subscription={{ value: true }}>
                {({ input }) => (
                  <Switch
                    data-testid="consent"
                    checked={input.value}
                    onChange={input.onChange}
                    labelClassName="text-xxs text-low mt-1"
                  >
                    <FormattedMessage
                      id="basket.footer.basketConsent"

                      defaultMessage="I have consented to the execution of the contract before the withdrawal period ends and acknowledge that the right to withdraw will expire upon the commencement of the contract’s execution."
                    />
                  </Switch>
                )}
              </Field>
            )}
            <Field name={`${FormKeys.POLICIES}.basketGTC`} subscription={{ value: true }}>
              {({ input }) => (
                <Switch
                  data-testid="GTC"
                  checked={input.value}
                  onChange={input.onChange}
                  labelClassName="text-xxs text-low mt-1"
                  className="mt-xs"
                >
                  <FormattedMessage
                    id="basket.footer.basketGTC"
                    defaultMessage="I accept the <gtc>general terms and conditions</gtc>."
                    values={{
                      gtc: getHyperlink({
                        'data-testid': 'general-terms',
                        href: generalTermsConditionsLink,
                        className: 'text-default',
                      }),
                    }}
                  />
                </Switch>
              )}
            </Field>
          </div>
        )}
      </main>
      <PageFooter
        hideNextButton
        onPrev={onPrev}
        shouldDisplayBackButton={shouldDisplayBackButton}
      >
        <Button
          testId="next-step"
          onClick={handleSubmit}
          disabledStyle={hasValidationErrors}
          className="footer-button"
        >
          {isBasketEmpty && <FormattedMessage id="steps.next" defaultMessage="Next" />}
          {!isBasketEmpty && <FormattedMessage id="basket.next" defaultMessage="Order with payment obligation" />}
        </Button>
      </PageFooter>
    </div>
  );
};

export default Step;
